import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Download, FileArchive, Repeat } from 'lucide-react';
import { useFormatConversion, type ImageFormat } from '@/hooks/useFormatConversion';
import type { UploadedImage } from '@/pages/ImageToolsHub';

interface FormatConversionToolProps {
  images: UploadedImage[];
}

const FORMAT_OPTIONS: { value: ImageFormat; label: string; description: string }[] = [
  { value: 'png', label: 'PNG', description: 'Lossless with transparency' },
  { value: 'jpg', label: 'JPG', description: 'Compressed, smaller size' },
  { value: 'webp', label: 'WEBP', description: 'Modern, efficient' },
  { value: 'bmp', label: 'BMP', description: 'Uncompressed bitmap' },
  { value: 'gif', label: 'GIF', description: 'Limited colors, animations' },
  { value: 'svg', label: 'SVG', description: 'Vector format (embedded)' },
  { value: 'pdf', label: 'PDF', description: 'Document format' },
];

export const FormatConversionTool: React.FC<FormatConversionToolProps> = ({ images }) => {
  const [selectedFormats, setSelectedFormats] = useState<ImageFormat[]>(['png']);
  const [quality, setQuality] = useState(92);
  
  const { isConverting, progress, convertedImages, convertBatch, downloadAsZip, clearResults } = useFormatConversion();
  
  const selectedImages = images.filter(img => img.selected);

  const handleFormatToggle = (format: ImageFormat) => {
    setSelectedFormats(prev =>
      prev.includes(format)
        ? prev.filter(f => f !== format)
        : [...prev, format]
    );
  };

  const handleConvert = async () => {
    if (selectedImages.length === 0 || selectedFormats.length === 0) return;
    
    const files = selectedImages.map(img => img.file);
    await convertBatch(files, {
      formats: selectedFormats,
      quality: quality / 100,
    });
  };

  const handleDownloadSingle = (url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Format Conversion Settings</CardTitle>
          <CardDescription>
            Select target formats and quality settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Label>Target Formats</Label>
            <div className="grid grid-cols-2 gap-4">
              {FORMAT_OPTIONS.map((format) => (
                <div key={format.value} className="flex items-start space-x-3">
                  <Checkbox
                    id={format.value}
                    checked={selectedFormats.includes(format.value)}
                    onCheckedChange={() => handleFormatToggle(format.value)}
                  />
                  <div className="grid gap-1 leading-none">
                    <label
                      htmlFor={format.value}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {format.label}
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {format.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="quality">Quality: {quality}%</Label>
              <Slider
                id="quality"
                min={1}
                max={100}
                step={1}
                value={[quality]}
                onValueChange={(value) => setQuality(value[0])}
              />
              <p className="text-xs text-muted-foreground">
                Higher quality = larger file size
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleConvert}
              disabled={selectedImages.length === 0 || selectedFormats.length === 0 || isConverting}
              className="flex-1"
            >
              <Repeat className="w-4 h-4 mr-2" />
              {isConverting ? 'Converting...' : `Convert (${selectedImages.length} to ${selectedFormats.length} formats)`}
            </Button>
            
            {convertedImages.length > 0 && (
              <Button onClick={clearResults} variant="outline">
                Clear Results
              </Button>
            )}
          </div>

          {isConverting && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                {Math.round(progress)}% complete
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {convertedImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Converted Files ({convertedImages.length})</CardTitle>
              <Button
                onClick={() => downloadAsZip(convertedImages)}
                variant="outline"
                size="sm"
              >
                <FileArchive className="w-4 h-4 mr-2" />
                Download as ZIP
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {convertedImages.map((img) => (
                <div
                  key={img.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={img.url}
                      alt={img.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                    <div>
                      <p className="text-sm font-medium">{img.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {img.format.toUpperCase()} • {formatBytes(img.size)}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDownloadSingle(img.url, img.name)}
                    variant="ghost"
                    size="sm"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
